import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Home, Newspaper, MessageCircle, User, Globe, CloudSun, CloudRain, Cloud, Sun, CloudSnow, CloudLightning, CloudDrizzle, Wind, Droplets, Thermometer, Eye, Search, Bell, LogOut, ChevronDown, Settings, Mic, Aperture, Heart, Users, Calendar } from 'lucide-react';
import { useClientAuth } from '../../context/ClientAuthContext';
import { clientMessagesAPI, clientProfilesAPI, clientTrendsAPI } from '../../services/clientApi';
import { ClientRouteSkeleton } from '../client/LuxeSkeletons';
import clientApi from '../../services/clientApi';
import { useLanguage } from '../../context/LanguageContext';
import { toPacificWallClock, pacificAbbr } from '../../utils/pacificTime';
import { isInvalidPhotoUrl } from '../../utils/photoUtils';
import { getLocationSilently } from '../../utils/silentLocation';
import { L, LuxeStyles, DiamondMark } from '../../theme/luxe';

// Client portal design tokens — mapped onto the Luxe editorial system
// (src/theme/luxe.js) so every THEME.* reference below follows it.
const THEME = {
  primary: L.ink,
  secondary: L.gold,
  navy: L.ink,
  background: L.paper,
  white: L.card,
  gray1: L.paper2,
  gray2: L.mute,
  gray3: L.line,
  border: L.line,
  green: L.online,
  amber: L.gold,
  purple: L.goldDeep,
  radius: L.radius,
  shadow: L.shadow,
  font: L.sans,
};

const DESKTOP_BREAKPOINT = 900;
const TAB_BAR_HEIGHT = 64;
const TOP_BAR_HEIGHT = 76;

// Hook to track window width
const useIsDesktop = () => {
  const [isDesktop, setIsDesktop] = useState(
    typeof window !== 'undefined' ? window.innerWidth >= DESKTOP_BREAKPOINT : false
  );

  useEffect(() => {
    const handleResize = () => setIsDesktop(window.innerWidth >= DESKTOP_BREAKPOINT);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return isDesktop;
};

// Weather code to icon & description mapping
const getWeatherInfo = (code, t) => {
  if (code === undefined || code === null) return { icon: CloudSun, desc: '' };
  if (code === 0) return { icon: Sun, desc: t('weather.clear', 'Clear') };
  if (code <= 3) return { icon: Cloud, desc: t('weather.cloudy', 'Cloudy') };
  if (code <= 49) return { icon: Cloud, desc: t('weather.foggy', 'Foggy') };
  if (code <= 59) return { icon: CloudDrizzle, desc: t('weather.drizzle', 'Drizzle') };
  if (code <= 69) return { icon: CloudRain, desc: t('weather.rain', 'Rain') };
  if (code <= 79) return { icon: CloudSnow, desc: t('weather.snow', 'Snow') };
  if (code <= 84) return { icon: CloudRain, desc: t('weather.showers', 'Showers') };
  if (code <= 86) return { icon: CloudSnow, desc: t('weather.snowfall', 'Snowfall') };
  if (code <= 99) return { icon: CloudLightning, desc: t('weather.thunderstorm', 'Thunderstorm') };
  return { icon: CloudSun, desc: '' };
};

// ─── DateTime + Weather Widget ──────────────────────────────────
const DateTimeWeatherWidget = ({ t, language }) => {
  const [now, setNow] = useState(new Date());
  const [weather, setWeather] = useState(null);
  const [weatherLoading, setWeatherLoading] = useState(true);
  const [weatherExpanded, setWeatherExpanded] = useState(false);

  // Real-time clock
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Fetch weather via Open-Meteo (free, no API key)
  useEffect(() => {
    const fetchWeather = async (lat, lon) => {
      try {
        const res = await fetch(
          `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current=temperature_2m,relative_humidity_2m,weather_code,wind_speed_10m,apparent_temperature&timezone=auto`
        );
        const data = await res.json();
        if (data.current) {
          setWeather({
            temp: Math.round(data.current.temperature_2m),
            feelsLike: Math.round(data.current.apparent_temperature),
            humidity: data.current.relative_humidity_2m,
            windSpeed: Math.round(data.current.wind_speed_10m),
            code: data.current.weather_code,
            unit: '°C',
          });
        }
      } catch (err) {
        console.error('Weather fetch failed:', err);
      } finally {
        setWeatherLoading(false);
      }
    };

    // Never prompts for location (see utils/silentLocation); Beijing when the
    // member hasn't already granted it.
    getLocationSilently({ lat: 39.9, lon: 116.4 }).then(({ lat, lon }) => fetchWeather(lat, lon));
  }, []);

  // Format pieces — the header clock runs on US Pacific like every other time
  // in the app, not on the viewer's own machine.
  const nowPT = toPacificWallClock(now);
  const year = nowPT.getFullYear();
  const monthNum = nowPT.getMonth(); // 0-indexed
  const dayNum = nowPT.getDate();
  const hours = String(nowPT.getHours()).padStart(2, '0');
  const minutes = String(nowPT.getMinutes()).padStart(2, '0');
  const seconds = String(nowPT.getSeconds()).padStart(2, '0');

  // English month names
  const EN_MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
  // Ordinal suffix
  const getOrdinal = (d) => {
    if (d > 3 && d < 21) return 'th';
    switch (d % 10) { case 1: return 'st'; case 2: return 'nd'; case 3: return 'rd'; default: return 'th'; }
  };
  // Language-aware date string
  const dateDisplay = language === 'en'
    ? `${EN_MONTHS[monthNum]} ${dayNum}${getOrdinal(dayNum)}, ${year}`
    : `${year}\u5E74${monthNum + 1}\u6708${dayNum}\u65E5`;

  // The badge names the clock's zone, which is always Pacific — PDT in summer,
  // PST in winter — rather than wherever the viewer happens to be sitting.
  const tzShort = pacificAbbr(now);

  const weatherInfo = weather ? getWeatherInfo(weather.code, t) : null;
  const WeatherIcon = weatherInfo?.icon || CloudSun;

  return (
    <div style={widgetStyles.container}>
      {/* Weather (first) */}
      {!weatherLoading && weather && (
        <div
          className="cl-weather-widget"
          style={widgetStyles.weatherRow}
          onClick={() => setWeatherExpanded(!weatherExpanded)}
        >
          <div className="cl-weather-icon-wrap" style={widgetStyles.weatherIconWrap}>
            <WeatherIcon size={16} color={THEME.primary} strokeWidth={2} />
          </div>
          <span style={widgetStyles.weatherTemp}>{weather.temp}{weather.unit}</span>
          <span style={widgetStyles.weatherDesc}>{weatherInfo.desc}</span>

          {/* Expandable detail */}
          {weatherExpanded && (
            <div className="cl-weather-detail" style={widgetStyles.weatherDetail}>
              <div style={widgetStyles.weatherDetailItem}>
                <Thermometer size={11} color={THEME.secondary} />
                <span>{t('weather.feelsLike', 'Feels')} {weather.feelsLike}{weather.unit}</span>
              </div>
              <div style={widgetStyles.weatherDetailItem}>
                <Droplets size={11} color="#60A5FA" />
                <span>{weather.humidity}%</span>
              </div>
              <div style={widgetStyles.weatherDetailItem}>
                <Wind size={11} color={THEME.gray2} />
                <span>{weather.windSpeed} km/h</span>
              </div>
            </div>
          )}
        </div>
      )}

      {weatherLoading && (
        <div style={widgetStyles.weatherRow}>
          <div className="cl-weather-loading" style={widgetStyles.weatherLoading} />
        </div>
      )}

      {/* Date & Time */}
      <div style={widgetStyles.dateTimeRow}>
        <div style={widgetStyles.dateBlock}>
          <span style={widgetStyles.dateText}>{dateDisplay}</span>
        </div>
        <div style={widgetStyles.separator} />
        <div style={widgetStyles.timeBlock}>
          <span className="cl-time-digit" style={widgetStyles.timeDigit}>{hours}</span>
          <span className="cl-time-colon" style={widgetStyles.timeColon}>:</span>
          <span className="cl-time-digit" style={widgetStyles.timeDigit}>{minutes}</span>
          <span className="cl-time-colon" style={widgetStyles.timeColon}>:</span>
          <span className="cl-time-digit cl-time-seconds" style={widgetStyles.timeSeconds}>{seconds}</span>
        </div>
        <div style={widgetStyles.tzBadge}>
          <Globe size={10} color={THEME.gray2} />
          <span style={widgetStyles.tzText}>{tzShort}</span>
        </div>
      </div>
    </div>
  );
};

const widgetStyles = {
  container: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    flexShrink: 0,
  },
  dateTimeRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
  dateBlock: {
    display: 'flex',
    alignItems: 'center',
  },
  dateText: {
    fontSize: '0.9062rem',
    fontWeight: 600,
    color: THEME.navy,
    fontVariantNumeric: 'tabular-nums',
    letterSpacing: '0.5px',
  },
  separator: {
    width: 1,
    height: 16,
    background: THEME.gray3,
    marginLeft: 2,
    marginRight: 2,
  },
  timeBlock: {
    display: 'flex',
    alignItems: 'baseline',
    gap: 1,
  },
  timeDigit: {
    fontSize: '1rem',
    fontWeight: 700,
    color: THEME.navy,
    fontVariantNumeric: 'tabular-nums',
    letterSpacing: '0.5px',
    display: 'inline-block',
    minWidth: '1.1em',
    textAlign: 'center',
  },
  timeColon: {
    fontSize: '1rem',
    fontWeight: 700,
    color: THEME.primary,
    margin: '0 1px',
  },
  timeSeconds: {
    fontSize: '0.8438rem',
    fontWeight: 600,
    color: THEME.gray2,
    minWidth: '1.2em',
  },
  tzBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: 3,
    background: THEME.gray1,
    padding: '2px 6px',
    borderRadius: 6,
    marginLeft: 2,
  },
  tzText: {
    fontSize: '0.7812rem',
    fontWeight: 500,
    color: THEME.gray2,
    maxWidth: 60,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  weatherRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 5,
    cursor: 'pointer',
    padding: '3px 8px',
    borderRadius: 10,
    background: 'rgba(232,49,145,0.04)',
    transition: 'all .3s',
    position: 'relative',
    flexWrap: 'wrap',
  },
  weatherIconWrap: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  weatherTemp: {
    fontSize: '0.9375rem',
    fontWeight: 700,
    color: THEME.navy,
  },
  weatherDesc: {
    fontSize: '0.8438rem',
    fontWeight: 500,
    color: THEME.gray2,
  },
  weatherDetail: {
    display: 'flex',
    gap: 8,
    width: '100%',
    paddingTop: 4,
    marginTop: 2,
    borderTop: `1px solid ${THEME.border}`,
  },
  weatherDetailItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 3,
    fontSize: '0.8125rem',
    color: THEME.gray2,
    fontWeight: 500,
  },
  weatherLoading: {
    width: 40,
    height: 14,
    borderRadius: 4,
    background: `linear-gradient(90deg, ${THEME.gray1}, ${THEME.gray3}, ${THEME.gray1})`,
    backgroundSize: '200% 100%',
    animation: 'clShimmer 1.5s ease-in-out infinite',
  },
};

// ─── Global Search Component ────────────────────────────────────
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const getSearchPhotoUrl = (photo) => {
  if (!photo) return null;
  if (typeof photo !== 'string') return null;
  if (photo.startsWith('data:') || photo.startsWith('http')) return photo;
  return `${API_URL}${photo.startsWith('/') ? '' : '/'}${photo}`;
};

const GlobalSearch = ({ t, navigate, width = 230, autoFocus = false }) => {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState({ profiles: [], trends: [], messages: [] });
  const wrapRef = useRef(null);
  const debounceRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Debounced search
  useEffect(() => {
    if (!query.trim()) {
      setResults({ profiles: [], trends: [], messages: [] });
      setOpen(false);
      return;
    }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const [profileRes, trendRes, msgRes] = await Promise.allSettled([
          clientProfilesAPI.browse({ search: query.trim(), limit: 5 }),
          clientTrendsAPI.getTrends({ search: query.trim(), limit: 5 }),
          clientMessagesAPI.getConversations(),
        ]);

        const profiles = profileRes.status === 'fulfilled'
          ? (profileRes.value.data?.profiles || profileRes.value.data?.data || []).slice(0, 5)
          : [];

        const trends = trendRes.status === 'fulfilled'
          ? (trendRes.value.data?.trends || []).slice(0, 5)
          : [];

        // Filter conversations client-side by participant name or last message
        const allConvos = msgRes.status === 'fulfilled'
          ? (msgRes.value.data?.conversations || [])
          : [];
        const q = query.trim().toLowerCase();
        const messages = allConvos.filter(c => {
          const participants = (c.participants || []).map(p =>
            (p.name || `${p.first_name || ''} ${p.last_name || ''}`.trim()).toLowerCase()
          );
          const matchName = participants.some(n => n.includes(q));
          const matchMsg = (c.last_message || '').toLowerCase().includes(q);
          const matchSubject = (c.subject || '').toLowerCase().includes(q);
          return matchName || matchMsg || matchSubject;
        }).slice(0, 5);

        setResults({ profiles, trends, messages });
        setOpen(true);
      } catch (err) {
        console.error('Search failed:', err);
      } finally {
        setLoading(false);
      }
    }, 350);

    return () => clearTimeout(debounceRef.current);
  }, [query]);

  const hasResults = results.profiles.length > 0 || results.trends.length > 0 || results.messages.length > 0;

  const handleNavigate = (path) => {
    setOpen(false);
    setQuery('');
    navigate(path);
  };

  return (
    <div ref={wrapRef} style={{ position: 'relative', width, flexShrink: 0 }}>
      <div style={searchStyles.wrap}>
        <Search size={15} color={L.ink} strokeWidth={1.4} />
        <input
          type="text"
          placeholder={t('nav.searchPlaceholder', 'Search profiles, trends, messages...')}
          aria-label="Search"
          autoFocus={autoFocus}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => { if (query.trim() && hasResults) setOpen(true); }}
          style={searchStyles.input}
          className="cl-top-search"
        />
        {loading && <div style={searchStyles.miniSpinner} />}
        {!loading && query && (
          <button onClick={() => { setQuery(''); setOpen(false); }} style={searchStyles.clearBtn}>
            <span style={{ fontSize: 16, lineHeight: 1, color: THEME.gray2 }}>✕</span>
          </button>
        )}
      </div>

      {/* Dropdown */}
      {open && query.trim() && (
        <div style={searchStyles.dropdown} className="cl-search-dropdown">
          {!hasResults && !loading && (
            <div style={searchStyles.empty}>{t('nav.searchNoResults')}</div>
          )}

          {/* Profiles */}
          {results.profiles.length > 0 && (
            <div>
              <div style={searchStyles.sectionLabel}>{t('nav.searchProfiles')}</div>
              {results.profiles.map(p => {
                const name = [p.first_name, p.last_name].filter(Boolean).join(' ') || p.name || p.nickname || '—';
                const photo = getSearchPhotoUrl(p.profile_photo || p.avatar);
                const age = p.age || (p.date_of_birth ? Math.floor((Date.now() - new Date(p.date_of_birth).getTime()) / 31557600000) : null);
                const loc = [p.location_city, p.location_state].filter(Boolean).join(', ');
                return (
                  <div
                    key={`p-${p.id}`}
                    style={searchStyles.item}
                    className="cl-search-item"
                    onClick={() => handleNavigate(`/client?profile=${p.id}`)}
                  >
                    <div style={{
                      ...searchStyles.avatar,
                      background: photo ? `url(${photo}) center/cover no-repeat` : `linear-gradient(135deg, ${THEME.secondary}, ${THEME.primary})`,
                    }}>
                      {!photo && <span style={searchStyles.avatarInitial}>{name.charAt(0).toUpperCase()}</span>}
                    </div>
                    <div style={searchStyles.itemInfo}>
                      <div style={searchStyles.itemName}>
                        {name}
                        {age && <span style={searchStyles.itemMeta}> · {age}</span>}
                      </div>
                      {loc && <div style={searchStyles.itemSub}>{loc}</div>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Trends */}
          {results.trends.length > 0 && (
            <div>
              <div style={searchStyles.sectionLabel}>{t('nav.searchTrends')}</div>
              {results.trends.map(tr => {
                const authorName = tr.author_name || [tr.author_first_name, tr.author_last_name].filter(Boolean).join(' ') || '';
                const preview = tr.title || (tr.content ? (tr.content.length > 60 ? tr.content.slice(0, 60) + '...' : tr.content) : '');
                return (
                  <div
                    key={`t-${tr.id}`}
                    style={searchStyles.item}
                    className="cl-search-item"
                    onClick={() => handleNavigate(`/client/trends/${tr.id}`)}
                  >
                    <div style={searchStyles.iconCircle}>
                      <Newspaper size={16} color={L.ink} strokeWidth={1.3} />
                    </div>
                    <div style={searchStyles.itemInfo}>
                      <div style={searchStyles.itemName}>{preview || 'Trend'}</div>
                      {authorName && <div style={searchStyles.itemSub}>{authorName}{tr.category_name ? ` · ${tr.category_name}` : ''}</div>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Messages */}
          {results.messages.length > 0 && (
            <div>
              <div style={searchStyles.sectionLabel}>{t('nav.searchMessages')}</div>
              {results.messages.map(c => {
                const other = (c.participants || []).find(p => p.name || p.first_name);
                const name = other ? (other.name || `${other.first_name || ''} ${other.last_name || ''}`.trim()) : (c.subject || 'Chat');
                const photo = other ? getSearchPhotoUrl(other.photo || other.avatar) : null;
                const preview = c.last_message ? (c.last_message.length > 50 ? c.last_message.slice(0, 50) + '...' : c.last_message) : '';
                return (
                  <div
                    key={`m-${c.id}`}
                    style={searchStyles.item}
                    className="cl-search-item"
                    onClick={() => handleNavigate(`/client/messages/${c.id}`)}
                  >
                    <div style={{
                      ...searchStyles.avatar,
                      background: photo ? `url(${photo}) center/cover no-repeat` : `linear-gradient(135deg, ${THEME.purple}, ${THEME.primary})`,
                    }}>
                      {!photo && <MessageCircle size={16} color="#fff" />}
                    </div>
                    <div style={searchStyles.itemInfo}>
                      <div style={searchStyles.itemName}>{name}</div>
                      {preview && <div style={searchStyles.itemSub}>{preview}</div>}
                    </div>
                    {c.unread_count > 0 && (
                      <span style={searchStyles.unreadBadge}>{c.unread_count}</span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const searchStyles = {
  wrap: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    background: 'transparent',
    borderBottom: `1px solid ${L.line}`,
    padding: '8px 2px',
    flex: 1,
    transition: `border-color .3s ${L.ease}`,
  },
  input: {
    border: 'none',
    outline: 'none',
    background: 'transparent',
    fontSize: 14.5,
    fontWeight: 300,
    letterSpacing: '.04em',
    color: L.ink,
    width: '100%',
    fontFamily: L.sans,
  },
  clearBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: 2,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  micBtn: {
    borderRadius: '50%',
    background: 'none',
    border: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    flexShrink: 0,
    padding: 0,
  },
  miniSpinner: {
    width: 14,
    height: 14,
    border: `1px solid ${L.line}`,
    borderTopColor: L.ink,
    borderRadius: '50%',
    animation: 'clientSpin 0.8s linear infinite',
    flexShrink: 0,
  },
  dropdown: {
    position: 'absolute',
    top: 'calc(100% + 14px)',
    right: 0,
    width: 380,
    background: L.card,
    borderRadius: L.radius,
    boxShadow: L.shadowLg,
    border: `1px solid ${L.line}`,
    maxHeight: 460,
    overflowY: 'auto',
    zIndex: 100002,
    padding: '8px 0 10px',
    animation: 'clDropdownIn .35s cubic-bezier(.16,1,.3,1)',
  },
  sectionLabel: {
    padding: '14px 20px 6px',
    fontFamily: L.sans,
    fontSize: 12,
    fontWeight: 500,
    color: L.mute,
    textTransform: 'uppercase',
    letterSpacing: '.28em',
  },
  item: {
    display: 'flex',
    alignItems: 'center',
    gap: 14,
    padding: '10px 20px',
    cursor: 'pointer',
    transition: 'background .25s',
  },
  avatar: {
    width: 40,
    height: 40,
    borderRadius: '50%',
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  avatarInitial: {
    color: L.paper,
    fontFamily: L.serif,
    fontSize: 18,
  },
  iconCircle: {
    width: 40,
    height: 40,
    borderRadius: '50%',
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: `1px solid ${L.line}`,
  },
  itemInfo: {
    flex: 1,
    minWidth: 0,
  },
  itemName: {
    fontFamily: L.serif,
    fontSize: 17,
    fontWeight: 500,
    color: L.ink,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  itemMeta: {
    fontWeight: 400,
    color: L.mute,
    fontSize: 15,
  },
  itemSub: {
    fontSize: 13,
    letterSpacing: '.08em',
    color: L.mute,
    marginTop: 2,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  unreadBadge: {
    minWidth: 18,
    height: 18,
    borderRadius: 9,
    background: L.ink,
    color: L.paper,
    fontSize: 12,
    fontWeight: 500,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0 5px',
    flexShrink: 0,
  },
  empty: {
    padding: '28px 20px',
    textAlign: 'center',
    fontFamily: L.serif,
    fontStyle: 'italic',
    fontSize: 17,
    color: L.mute,
  },
};

// ─── Language Switcher ──────────────────────────────────────────
const LanguageSwitcher = () => {
  const { language, setLanguage } = useLanguage();

  return (
    <div style={langStyles.wrapper}>
      <button
        onClick={() => setLanguage('en')}
        style={{
          ...langStyles.btn,
          ...(language === 'en' ? langStyles.btnActive : {}),
        }}
      >
        EN
      </button>
      <span style={{ width: 1, height: 10, background: L.line }} />
      <button
        onClick={() => setLanguage('zh')}
        style={{
          ...langStyles.btn,
          ...(language === 'zh' ? langStyles.btnActive : {}),
        }}
      >
        中
      </button>
    </div>
  );
};

const langStyles = {
  wrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: 2,
    flexShrink: 0,
  },
  btn: {
    padding: '4px 6px',
    fontFamily: L.sans,
    fontSize: 13,
    fontWeight: 400,
    letterSpacing: '.18em',
    border: 'none',
    cursor: 'pointer',
    background: 'none',
    color: L.mute,
    transition: 'color .3s',
    outline: 'none',
    lineHeight: '1.2',
  },
  btnActive: {
    color: L.ink,
    fontWeight: 600,
  },
};

// ─── Profile Dropdown ──────────────────────────────────────────
const ProfileDropdown = ({ clientUser, clientLogout, navigate, t }) => {
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);

  const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
  const getPhotoUrl = (photo) => {
    if (!photo || isInvalidPhotoUrl(photo)) return null;
    if (photo.startsWith('data:')) return photo;
    if (photo.startsWith('http')) return photo;
    if (photo.startsWith('/uploads/')) return `${API_BASE}${photo}`;
    return `${API_BASE}/uploads/${photo}`;
  };

  // /client/auth/me returns the photo nested under .profile, but some auth flows
  // flatten it onto the top level — check both shapes.
  const profileObj = clientUser?.profile || {};
  const photoUrl = getPhotoUrl(
    profileObj.profile_photo || profileObj.photo || profileObj.avatar
    || clientUser?.profile_photo || clientUser?.photo || clientUser?.avatar
  );
  const displayName = (profileObj.first_name || clientUser?.first_name)
    ? `${profileObj.first_name || clientUser?.first_name || ''} ${profileObj.last_name || clientUser?.last_name || ''}`.trim()
    : clientUser?.name || clientUser?.nickname || '';
  const initial = displayName ? displayName.charAt(0).toUpperCase() : '?';

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div ref={dropdownRef} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen(!open)}
        style={profileDropdownStyles.trigger}
        className="cl-profile-trigger"
      >
        {photoUrl ? (
          <img src={photoUrl} alt="" style={profileDropdownStyles.avatar} />
        ) : (
          <div style={profileDropdownStyles.avatarFallback}>{initial}</div>
        )}
        <ChevronDown size={13} color={L.ink} strokeWidth={1.3} style={{ transition: 'transform .35s', transform: open ? 'rotate(180deg)' : 'rotate(0)' }} />
      </button>

      {open && (
        <div style={profileDropdownStyles.menu} className="cl-profile-menu">
          <button
            style={profileDropdownStyles.menuItem}
            className="cl-profile-menu-item"
            onClick={() => { setOpen(false); navigate('/client/me/edit'); }}
          >
            <User size={15} color={L.ink} strokeWidth={1.3} />
            <span>{t('nav.myAccount', 'My Account')}</span>
          </button>
          <div style={profileDropdownStyles.divider} />
          <button
            style={{ ...profileDropdownStyles.menuItem, color: L.danger }}
            className="cl-profile-menu-item"
            onClick={() => { setOpen(false); clientLogout(); navigate('/login'); }}
          >
            <LogOut size={15} color={L.danger} strokeWidth={1.3} />
            <span>{t('nav.logout', 'Logout')}</span>
          </button>
        </div>
      )}
    </div>
  );
};

const profileDropdownStyles = {
  trigger: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: 2,
    borderRadius: 999,
    outline: 'none',
  },
  avatar: {
    width: 34,
    height: 34,
    borderRadius: '50%',
    objectFit: 'cover',
    border: `1px solid ${L.ink}`,
    padding: 2,
    background: L.paper,
  },
  avatarFallback: {
    width: 34,
    height: 34,
    borderRadius: '50%',
    background: L.ink,
    color: L.paper,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: L.serif,
    fontSize: 17,
  },
  menu: {
    position: 'absolute',
    top: 'calc(100% + 14px)',
    right: 0,
    background: L.card,
    borderRadius: L.radius,
    boxShadow: L.shadowLg,
    border: `1px solid ${L.line}`,
    minWidth: 210,
    padding: '8px 0',
    zIndex: 100001,
    animation: 'clDropdownIn .35s cubic-bezier(.16,1,.3,1)',
  },
  menuItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    width: '100%',
    padding: '12px 20px',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontFamily: L.sans,
    fontSize: 13,
    fontWeight: 500,
    letterSpacing: '.2em',
    textTransform: 'uppercase',
    color: L.ink,
    transition: 'background .25s',
    outline: 'none',
    textAlign: 'left',
  },
  divider: {
    height: 1,
    background: L.line,
    margin: '6px 20px',
  },
};

// ═══════════════════════════════════════════════════════════════
// Main Layout Component
// ═══════════════════════════════════════════════════════════════
const ClientLayout = () => {
  const { isClientAuthenticated, loading, clientUser, clientLogout } = useClientAuth();
  const { t, language } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const [unreadCount, setUnreadCount] = useState(0);
  const isDesktop = useIsDesktop();
  // The masthead tightens as the window narrows: below 1360px the search box
  // folds into an icon, below 1150px the nav spacing and wordmark tighten.
  const [vw, setVw] = useState(() => (typeof window !== 'undefined' ? window.innerWidth : 1440));
  useEffect(() => {
    const h = () => setVw(window.innerWidth);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  const compact = vw < 1360;
  const tight = vw < 1150;
  const [searchOpen, setSearchOpen] = useState(false);
  const searchPanelRef = useRef(null);
  useEffect(() => { setSearchOpen(false); }, [location.pathname, location.search]);
  useEffect(() => {
    if (!searchOpen) return undefined;
    const onDown = (e) => { if (searchPanelRef.current && !searchPanelRef.current.contains(e.target)) setSearchOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setSearchOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [searchOpen]);
  // Per-page scroll context — each route mounts a fresh container and resets
  // scrollTop to 0 on route change, so the next page doesn't inherit the prior
  // page's scroll position.
  const mainScrollRef = useRef(null);
  useEffect(() => {
    if (mainScrollRef.current) {
      mainScrollRef.current.scrollTop = 0;
    }
    // Also reset window scroll on mobile in case the page bypassed mainContent.
    try { window.scrollTo({ top: 0, behavior: 'auto' }); } catch (e) { /* ignore */ }
  }, [location.pathname]);

  // Desktop sidebar nav items (icon-only, Pinterest style)
  const zh = language === 'zh';
  const sidebarItems = [
    { key: 'home', icon: Home, route: '/client', tooltip: t('nav.home', 'Home'), label: zh ? t('nav.home', '首页') : 'Home' },
    { key: 'events', icon: Aperture, route: '/client/discover', match: ['/client/discover', '/client/events', '/client/programs', '/client/trends'], tooltip: language === 'en' ? 'Moments' : '动态', label: language === 'en' ? 'Moments' : '动态' },
    { key: 'messages', icon: MessageCircle, route: '/client/messages', tooltip: t('nav.messages', 'Messages'), label: zh ? t('nav.messages', '消息') : 'Messages' },
    { key: 'visitors', icon: Eye, route: '/client/me/visitors', tooltip: t('clientDash.whoViewedMe', 'Who Viewed Me'), label: zh ? t('clientDash.whoViewedMe', '谁看过我') : 'Visitors' },
    { key: 'following', icon: Heart, route: '/client/me/following', tooltip: t('clientDash.followingFans', 'Following & Fans'), label: zh ? t('clientDash.followingFans', '关注与粉丝') : 'Following' },
    { key: 'matches', icon: Users, route: '/client/me/matches', tooltip: t('clientDash.matchmaking', 'Matchmaking'), label: zh ? t('clientDash.matchmaking', '匹配牵线') : 'Matches' },
    { key: 'schedules', icon: Calendar, route: '/client/me/schedules', tooltip: t('clientDash.dateSchedule', 'Date Schedule'), label: zh ? t('clientDash.dateSchedule', '约会日程') : 'Dates' },
  ];

  // Mobile tabs
  const mobileTabs = [
    { key: 'home', label: t('nav.home', '首页'), icon: Home, route: '/client' },
    { key: 'events', label: language === 'en' ? 'Moments' : '动态', icon: Newspaper, route: '/client/discover', match: ['/client/discover', '/client/events', '/client/programs', '/client/trends'] },
    { key: 'messages', label: t('nav.messages', '消息'), icon: MessageCircle, route: '/client/messages' },
    { key: 'profile', label: t('nav.profile', '我的'), icon: User, route: '/client/me' },
  ];

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!loading && !isClientAuthenticated) {
      navigate('/login', { replace: true });
    }
  }, [loading, isClientAuthenticated, navigate]);

  // Fetch unread message count
  const fetchUnreadCount = useCallback(async () => {
    try {
      const response = await clientMessagesAPI.getUnreadCount();
      setUnreadCount(response.data?.unread_count || 0);
    } catch (err) {
      console.error('Failed to fetch unread count:', err);
    }
  }, []);

  useEffect(() => {
    if (isClientAuthenticated) {
      fetchUnreadCount();
      const interval = setInterval(fetchUnreadCount, 30000);
      return () => clearInterval(interval);
    }
  }, [isClientAuthenticated, fetchUnreadCount]);

  // Boot the PWA service worker once on first authenticated render. Then
  // try to subscribe to web push so the backend can deliver new-message
  // notifications. Each call is idempotent so re-running on re-login is OK.
  useEffect(() => {
    if (!isClientAuthenticated) return;
    let cancelled = false;
    (async () => {
      try {
        const { registerServiceWorker, ensurePushSubscription } = await import('../../utils/pushNotifications');
        await registerServiceWorker();
        if (cancelled) return;
        // Defer the permission prompt a few seconds so it doesn't fire at
        // the same instant as the initial render — feels less intrusive.
        setTimeout(() => { if (!cancelled) ensurePushSubscription(); }, 4000);
      } catch (e) { /* push is best-effort */ }
    })();
    return () => { cancelled = true; };
  }, [isClientAuthenticated]);

  // Determine active tab
  const getIsActive = (route, match) => {
    if (Array.isArray(match)) return match.some((m) => location.pathname.startsWith(m));
    if (route === '/client') {
      return location.pathname === '/client' || location.pathname === '/client/';
    }
    if (route === '/client/me') {
      return location.pathname === '/client/me' || location.pathname.startsWith('/client/me/');
    }
    return location.pathname.startsWith(route);
  };

  // Skeleton placeholder while the auth context is checking the token. Gives
  // the user structured shimmer blocks instead of an empty / spinning page.
  if (loading) {
    return <ClientRouteSkeleton pathname={location.pathname} />;
  }

  if (!isClientAuthenticated) {
    return null;
  }

  const isChat = /^\/client\/messages\/[^/]+$/.test(location.pathname);

  // ──── Desktop Layout — editorial masthead ───────────────────
  if (isDesktop) {
    return (
      <div className="lx-root lx-grain" style={styles.desktopWrapper}>
        <LuxeStyles />
        {/* No 安装 FindU 到主屏 prompt on the member portal (removed per request). */}

        <header style={styles.masthead}>
          <div style={styles.mastheadInner}>
            {/* Wordmark */}
            {/* Logo: diamond + FINDU on one line */}
            <button onClick={() => navigate('/client')} style={styles.wordmark} aria-label="FINDU" className="cl-wordmark">
              <span style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <DiamondMark size={tight ? 28 : 30} strokeWidth={1.25} />
                <span style={styles.wordmarkMain}>FINDU</span>
              </span>
            </button>

            {/* Section index */}
            <nav className="lx-noscroll" style={styles.mastNav}>
              <div style={{ display: 'flex', alignItems: 'center', gap: tight ? 18 : 26, margin: '0 auto' }}>
              {sidebarItems.map((item) => {
                const isActive = getIsActive(item.route, item.match);
                return (
                  <button
                    key={item.key}
                    onClick={() => navigate(item.route)}
                    style={{
                      ...styles.mastNavItem, color: isActive ? L.ink : L.ink2, fontWeight: isActive ? 600 : 400,
                      ...(tight ? { letterSpacing: zh ? '.14em' : '.12em' } : {}),
                    }}
                    aria-label={item.tooltip}
                    title={item.tooltip}
                  >
                    <span className={`lx-link${isActive ? ' lx-active' : ''}`}>{item.label}</span>
                    {item.key === 'messages' && unreadCount > 0 && (
                      <sup style={styles.mastBadge}>{unreadCount > 9 ? '9+' : unreadCount}</sup>
                    )}
                  </button>
                );
              })}
              </div>
            </nav>

            {/* Right: search, language, account */}
            <div style={styles.topRightGroup}>
              {compact ? (
                <div ref={searchPanelRef} style={{ position: 'relative' }}>
                  <button
                    type="button"
                    className="lx-icon-btn"
                    onClick={() => setSearchOpen((o) => !o)}
                    style={{ ...styles.settingsBtn, ...(searchOpen ? { background: L.ink, color: L.paper, borderColor: L.ink } : {}) }}
                    aria-label={t('nav.searchPlaceholder', 'Search')}
                    title={t('nav.searchPlaceholder', 'Search')}
                  >
                    <Search size={15} strokeWidth={1.3} />
                  </button>
                  {searchOpen && (
                    <div style={styles.searchPanel}>
                      <GlobalSearch t={t} navigate={navigate} width="100%" autoFocus />
                    </div>
                  )}
                </div>
              ) : (
                <GlobalSearch t={t} navigate={navigate} />
              )}
              <LanguageSwitcher />
              <button
                className="lx-icon-btn"
                onClick={() => navigate('/client/me')}
                style={{ ...styles.settingsBtn, ...(location.pathname === '/client/me' || location.pathname === '/client/me/settings' ? { background: L.ink, color: L.paper, borderColor: L.ink } : {}) }}
                title={t('nav.settings', 'Settings')}
                aria-label="Settings"
              >
                <Settings size={15} strokeWidth={1.3} />
              </button>
              <ProfileDropdown
                clientUser={clientUser}
                clientLogout={clientLogout}
                navigate={navigate}
                t={t}
              />
            </div>
          </div>
        </header>

        {/* Page content — scroll-reset on each route change so switching
            pages doesn't land mid-page from the previous view. */}
        <main
          key={location.pathname}
          ref={mainScrollRef}
          className="lx-fade"
          style={styles.desktopMainContent}
        >
          <Outlet />
        </main>
      </div>
    );
  }

  // ──── Mobile Layout ──────────────────────────────────────────
  return (
    <div className="lx-root lx-grain" style={styles.layoutWrapper}>
      <LuxeStyles />
      {/* No 安装到主屏幕 prompt on the mobile web portal (removed per request). */}
      {/* Page content — each route gets its own scroll context (key-reset) */}
      <main
        key={location.pathname}
        ref={mainScrollRef}
        className="lx-fade"
        style={{
          ...styles.mainContent,
          // Chat page hides the bottom tab bar, so drop the tab-bar bottom
          // padding there (no blank strip under the input).
          ...(isChat ? { paddingBottom: 0 } : {}),
        }}
      >
        <Outlet />
      </main>

      {/* Bottom tab bar — hidden on immersive detail pages that render their own bottom bar,
          and hidden via CSS while ClientHome's profile-detail modal is open
          (body gets the `.cl-detail-open` class). */}
      {!/^\/client\/trends\/(\d+|new)/.test(location.pathname) && (
      <nav className="cl-bottom-tabbar" style={styles.tabBar}>
        <div style={styles.tabBarInner}>
          {mobileTabs.map((tab) => {
            const isActive = getIsActive(tab.route, tab.match);
            const IconComponent = tab.icon;
            return (
              <button
                key={tab.key}
                onClick={() => navigate(tab.route)}
                style={styles.tabItem}
                aria-label={tab.label}
              >
                <div style={{ position: 'relative', display: 'inline-flex' }}>
                  <IconComponent
                    size={21}
                    color={isActive ? L.ink : L.mute}
                    strokeWidth={isActive ? 1.6 : 1.2}
                  />
                  {tab.key === 'messages' && unreadCount > 0 && (
                    <span style={styles.badge}>
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  )}
                </div>
                <span style={{
                  ...styles.tabLabel,
                  color: isActive ? L.ink : L.mute,
                  fontWeight: isActive ? 600 : 400,
                }}>
                  {tab.label}
                </span>
              </button>
            );
          })}
        </div>
      </nav>
      )}
    </div>
  );
};

const styles = {
  // ──── Desktop — masthead ─────────────────────────────────────
  desktopWrapper: {
    fontFamily: L.sans,
    background: L.paper,
    height: '100vh',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  masthead: {
    position: 'relative',
    // Above every in-page sticky bar (≤ 200) so the account / search
    // dropdowns that hang below it are never covered; below every page
    // overlay (≥ 500) so modals still sit on top.
    zIndex: 300,
    flexShrink: 0,
    background: 'rgba(246,243,238,.86)',
    backdropFilter: 'saturate(140%) blur(14px)',
    WebkitBackdropFilter: 'saturate(140%) blur(14px)',
    borderBottom: `1px solid ${L.line}`,
  },
  mastheadInner: {
    height: TOP_BAR_HEIGHT,
    display: 'flex',
    alignItems: 'center',
    gap: 24,
    padding: '0 36px',
    maxWidth: 1600,
    margin: '0 auto',
    boxSizing: 'border-box',
  },
  wordmark: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    background: 'none',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
    flexShrink: 0,
  },
  wordmarkMain: {
    fontFamily: L.serif,
    fontSize: 30,
    fontWeight: 500,
    letterSpacing: '.32em',
    lineHeight: 1,
    color: L.ink,
  },
  mastNav: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    alignItems: 'center',
    overflowX: 'auto',
  },
  searchPanel: {
    position: 'absolute',
    top: 'calc(100% + 18px)',
    right: -60,
    width: 400,
    background: L.card,
    border: `1px solid ${L.line}`,
    boxShadow: L.shadowLg,
    padding: '16px 22px 18px',
    animation: 'clDropdownIn .35s cubic-bezier(.16,1,.3,1)',
  },
  mastNavItem: {
    position: 'relative',
    background: 'none',
    border: 'none',
    padding: '6px 0',
    cursor: 'pointer',
    fontFamily: L.sans,
    fontSize: 13,
    letterSpacing: '.2em',
    textTransform: 'uppercase',
    whiteSpace: 'nowrap',
    transition: 'color .3s',
  },
  mastBadge: {
    marginLeft: 4,
    fontSize: 11,
    fontWeight: 600,
    color: L.gold,
    letterSpacing: 0,
  },
  topRightGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: 18,
    flexShrink: 0,
  },
  settingsBtn: {
    width: 34,
    height: 34,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'transparent',
    border: `1px solid ${L.line}`,
    borderRadius: '50%',
    color: L.ink,
    cursor: 'pointer',
    transition: `all .3s ${L.ease}`,
  },
  desktopMainContent: {
    flex: 1,
    minHeight: 0,
    background: L.paper,
    // Give each page its own scroll context so navigating across pages
    // doesn't inherit the prior page's scroll position.
    overflowY: 'auto',
    position: 'relative',
  },

  // ──── Mobile ────────────────────────────────────────────────
  layoutWrapper: {
    fontFamily: L.sans,
    background: L.paper,
    minHeight: '100vh',
    maxWidth: 600,
    margin: '0 auto',
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
  },
  mainContent: {
    flex: 1,
    paddingTop: 0,
    paddingBottom: TAB_BAR_HEIGHT + 20,
    background: L.paper,
    minHeight: 0,
    // Each page owns its own scroll context so route changes don't inherit
    // the previous page's scroll position.
    overflowY: 'auto',
    WebkitOverflowScrolling: 'touch',
    height: '100vh',
    position: 'relative',
  },
  tabBar: {
    position: 'fixed',
    bottom: 0,
    left: '50%',
    transform: 'translateX(-50%)',
    width: '100%',
    maxWidth: 600,
    background: 'rgba(246,243,238,.88)',
    backdropFilter: 'saturate(140%) blur(16px)',
    WebkitBackdropFilter: 'saturate(140%) blur(16px)',
    borderTop: `1px solid ${L.line}`,
    zIndex: 100,
    paddingBottom: 'env(safe-area-inset-bottom, 0px)',
  },
  tabBarInner: {
    display: 'flex',
    alignItems: 'stretch',
    justifyContent: 'space-around',
    height: TAB_BAR_HEIGHT,
    padding: '0 8px',
  },
  tabItem: {
    position: 'relative',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 5,
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '6px 0',
    WebkitTapHighlightColor: 'transparent',
    outline: 'none',
  },
  tabLabel: {
    fontFamily: L.sans,
    fontSize: 11.5,
    letterSpacing: '.18em',
    textTransform: 'uppercase',
    lineHeight: '12px',
    whiteSpace: 'nowrap',
  },
  badge: {
    position: 'absolute',
    top: -5,
    right: -9,
    minWidth: 16,
    height: 16,
    borderRadius: 8,
    background: L.ink,
    color: L.paper,
    fontSize: 11,
    fontWeight: 500,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0 4px',
    lineHeight: '16px',
    boxSizing: 'border-box',
    border: `1px solid ${L.paper}`,
  },
};

// Inject keyframes
if (typeof document !== 'undefined') {
  const styleId = 'client-layout-keyframes';
  if (!document.getElementById(styleId)) {
    const styleEl = document.createElement('style');
    styleEl.id = styleId;
    styleEl.textContent = `
      @keyframes clientSpin { to { transform: rotate(360deg); } }
      @keyframes clDropdownIn {
        from { opacity: 0; transform: translateY(-8px); }
        to { opacity: 1; transform: translateY(0); }
      }
      .cl-profile-trigger:hover img { border-color: ${L.gold} !important; }
      .cl-profile-menu-item:hover { background: ${L.paper2} !important; }
      .cl-wordmark svg { transition: transform .8s cubic-bezier(.16,1,.3,1); }
      .cl-wordmark:hover svg { transform: rotate(-8deg) scale(1.06); }
      .cl-top-search::placeholder { color: ${L.mute}; font-weight: 300; }
      .cl-search-item:hover { background: ${L.paper} !important; }
      .cl-weather-widget { transition: all .3s ease; }
      .cl-weather-detail { animation: clDropdownIn .3s ease-out; }
    `;
    document.head.appendChild(styleEl);
  }
}

export default ClientLayout;
